import axios from 'axios';
import { useAuth } from 'services/auth';
import React, { createContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PORTAL_API_HOST } from '../../config';

// Main axios instance for making API requests
export const api = axios.create({
    baseURL: PORTAL_API_HOST,
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

// Special axios instance for making refresh token requests
export const apiRefresh = axios.create({
    baseURL: PORTAL_API_HOST,
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

const APIContext = createContext(null);

export const APIProvider = ({ children }) => {
    const auth = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // No need to register an interceptor until we've got a valid id token
        if (!auth.state.idToken) {
            return;
        }

        const interceptorId = api.interceptors.request.use(
            config => {
                const idToken = auth.state.idToken;

                if (idToken) {
                    config.headers.Authorization = `Bearer ${idToken}`;
                }

                return config;
            },
            error => {
                return Promise.reject(error);
            },
        );

        auth.actions.completeLogin();

        return () => {
            api.interceptors.request.eject(interceptorId);
        };
    }, [auth.state.idToken]);

    useEffect(() => {
        // No need to register an interceptor until we've got a valid refresh token
        if (!auth.state.refreshToken) {
            return;
        }

        const interceptorId = api.interceptors.response.use(
            response => {
                return response;
            },
            async error => {
                const originalRequest = error.config;

                if (error?.response?.status === 401) {
                    if (!originalRequest._retry) {
                        originalRequest._retry = true;

                        const newToken = await auth.actions.refreshToken(auth);

                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;

                            // Retry original request with new token
                            return axios(originalRequest)
                                .catch(error => {
                                    auth.actions.logout();
                                    navigate('/login');
                                });
                        }
                    }
                    else {
                        auth.actions.logout();
                        navigate('/login');
                    }
                }

                return Promise.reject(error);
            },
        );

        return () => {
            api.interceptors.response.eject(interceptorId);
        };
    }, [auth.state.refreshToken]);

    return (
        <APIContext.Provider value={null}> {children} </APIContext.Provider>
    );
};

APIProvider.propTypes = {
    children: PropTypes.node,
};